import { Injectable, effect } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../environments/environment';
import { ReconnectingSocket } from './reconnecting-socket';
import { isChequeEvent, OrderStatusEvent, SocketEventType, TableSocketEvent } from '../models/websocket.model';
import { Item, Menu, Section } from '../models/menu.model';
import { ItemStatus, OrderStatus } from '../shared/enums';
import { CartService } from './cart.service';
import { DataService } from './data.service';
import { MenuService } from '../menu/menu.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class TableSocketService extends ReconnectingSocket<TableSocketEvent> {

  // Tracks whether we've ever reached 'open' before, so the reconnect effect below can tell
  // a genuine reconnect (worth a refetch — we may have missed events while disconnected)
  // apart from the very first connect (redundant — app boot already loaded the menu).
  private hasConnectedBefore = false;

  constructor(
    private cartService: CartService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private dataService: DataService,
    private menuService: MenuService
  ) {
    super();
    this.message$.subscribe(event => this.handleEvent(event));

    // A reconnect may have silently swallowed a menu.updated event while we were
    // disconnected (e.g. a schedule edit that landed mid-drop), so refetch once reconnected
    // rather than staying stale until some unrelated future event happens to correct it.
    effect(() => {
      if (this.connectionStatus() !== 'open') return;

      if (this.hasConnectedBefore) {
        this.menuService.refresh();
      }
      this.hasConnectedBefore = true;
    });

    // Keeps cart lines in sync with whatever the resolved menu currently says — whether
    // that changed because MenuService refetched it (menu.updated, reconnect, rejected items), or
    // because MenuService's own schedule-boundary wakeup flipped a price/section with no
    // socket event involved at all (e.g. happy hour starting with nothing else changing).
    effect(() => {
      const menu = this.menuService.menu();
      if (menu) this.refreshCartItems(menu);
    });
  }

  public connectToTable(tableId: string): void {
    this.connect(`${environment.urls.ws}/table/${tableId}/`);
  }

  private handleEvent(event: TableSocketEvent): void {
    // Cheque events are handled by the pay-at-table pages themselves, which
    // subscribe to message$ directly since the reload needs page-local state.
    if (isChequeEvent(event)) {
      return;
    }

    switch (event.type) {
      case SocketEventType.OrderStatus:
        this.handleOrderStatus(event);
        return;
      case SocketEventType.PaymentConfirmed:
        // Broadcast table-wide, so gate on our own pending marker — otherwise a PAT bill
        // payment or another guest's payment would wipe an unrelated cart.
        if (event.user_id === this.dataService.getUserId() && this.cartService.getPendingPayment()) {
          this.cartService.clearCart();
        }
        return;
      case SocketEventType.MenuUpdated:
        this.menuService.refresh(event.zone_id);
        return;
    }
  }

  // Only the guest's own order — a staff action on someone else's order isn't this guest's concern.
  private handleOrderStatus(event: OrderStatusEvent): void {
    if (event.user_id !== this.dataService.getUserId()) {
      return;
    }

    switch (event.status) {
      case OrderStatus.Cancelled:
        this.showToast('ORDER.CANCELLED', 'warning');
        break;
      case OrderStatus.Void:
        this.showToast('ORDER.VOID', 'warning');
        break;
      case OrderStatus.Paid:
      case OrderStatus.Partial:
      case OrderStatus.Unpaid:
        break; // normal payment-flow transitions, not a staff action
      default:
        this.showToast('ORDER.UPDATED', 'info'); // no specific wording for this status — say something anyway
    }
  }

  // Doesn't touch an open modifier dialog — it only owns a point-in-time item snapshot.
  private refreshCartItems(menu: Menu): void {
    const freshItems = this.flattenItems(menu.sections);
    const freshItemsById = new Map(freshItems.map(item => [item.id, item]));

    const cartItemsById = new Map(this.cartService.cartLines().map(line => [line.item.id, line.item]));
    let anyUpdated = false;
    let anyRemoved = false;

    cartItemsById.forEach((currentItem, itemId) => {
      const freshItem = freshItemsById.get(itemId);
      if (freshItem && freshItem.status === ItemStatus.Enabled) {
        if (this.cartService.refreshItem(freshItem) && this.isMaterialChange(currentItem, freshItem)) {
          anyUpdated = true;
        }
      } else if (this.cartService.removeItem(itemId)) {
        anyRemoved = true; // missing or no longer Enabled — can't be ordered anymore
      }
    });

    // Modifiers carry no status flag of their own — an unavailable one is simply absent
    // from the fresh menu. Checked per cart line rather than per item id, since two lines
    // of the same item can carry different modifier selections.
    const freshModifierIds = new Set(
      freshItems.flatMap(item => item.modifier_groups.flatMap(group => group.modifiers.map(modifier => modifier.id)))
    );
    this.cartService.cartLines().forEach(line => {
      if (line.modifiers.some(modifier => !freshModifierIds.has(modifier.id))) {
        this.cartService.removeLine(line.id);
        anyRemoved = true;
      }
    });

    if (anyRemoved) {
      this.showToast('CART.ITEM_REMOVED', 'warning');
    } else if (anyUpdated) {
      this.showToast('CART.ITEM_UPDATED', 'info');
    }
  }

  private showToast(translationKey: string, type: 'info' | 'warning'): void {
    this.toastService[type](this.translateService.instant(translationKey));
  }

  private isMaterialChange(current: Item, fresh: Item): boolean {
    return current.price !== fresh.price || current.status !== fresh.status;
  }

  private flattenItems(sections: Section[]): Item[] {
    const items: Item[] = [];

    const visit = (section: Section): void => {
      items.push(...section.items);
      section.sub_sections.forEach(visit);
    };

    sections.forEach(visit);
    return items;
  }
}
